import {
  Box,
  Divider,
  HStack,
  Skeleton,
  Spinner,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useAtom } from 'jotai';
import { useEffect, useRef } from 'react';

import { JournalEntryCardSkeleton } from '../JournalEntryCardSkeleton/JournalEntryCardSkeleton';
import { JournalEntryDispatcher } from '../JournalEntryDispatcher/JournalEntryDispatcher';
import { JournalEntryErrorBoundary } from '../JournalEntryErrorBoundary/JournalEntryErrorBoundary';
import { allEntriesExpandedAtom } from '../timelineAtoms';
import { JournalEntry } from '../types';
import {
  formatDateHeader,
  groupEntriesByDate,
  resolveTimezone,
  sortDatesDescending,
} from '../utils';

export interface JournalTimelineProps {
  entries: JournalEntry[];
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  /** Date string (YYYY-MM-DD) of the last appointment. When provided, a
   *  horizontal indicator is rendered between the entries that came after
   *  this date and those on/before it. */
  lastAppointmentDate?: string | null;
}

/**
 * Main timeline component that displays journal entries grouped by date.
 * Supports infinite scrolling with loading states and global expand all toggle.
 */
export const JournalTimeline: React.FC<JournalTimelineProps> = ({
  entries,
  onLoadMore,
  hasMore = false,
  isLoading = false,
  isLoadingMore = false,
  lastAppointmentDate,
}) => {
  const [allExpanded, setAllExpanded] = useAtom(allEntriesExpandedAtom);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  // Infinite scroll using Intersection Observer
  useEffect(() => {
    if (!onLoadMore || !hasMore || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    const trigger = loadMoreTriggerRef.current;
    if (trigger) {
      observer.observe(trigger);
    }

    return () => {
      if (trigger) {
        observer.unobserve(trigger);
      }
    };
  }, [onLoadMore, hasMore, isLoadingMore]);

  // Initial loading state
  if (isLoading) {
    return (
      <VStack align="stretch" spacing={4} width="100%">
        {/* Mock date headers and skeleton cards */}
        {[1, 2].map((dateGroup) => (
          <Box key={dateGroup}>
            <Skeleton height="14px" width="120px" mb={2} />
            <VStack align="stretch" spacing={2}>
              {[1, 2, 3].map((card) => (
                <JournalEntryCardSkeleton key={`${dateGroup}-${card}`} />
              ))}
            </VStack>
            <Divider mt={4} />
          </Box>
        ))}
      </VStack>
    );
  }

  // Empty state
  if (entries.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
        p={4}
      >
        <Text fontSize="md" color="text.muted" textAlign="center">
          No journal entries yet
        </Text>
        <Text fontSize="sm" color="text.muted" textAlign="center" mt={2}>
          Journal entries will appear here once your patient starts tracking
        </Text>
      </Box>
    );
  }

  // Group entries by date
  const groupedEntries = groupEntriesByDate(entries);
  const sortedDates = sortDatesDescending(Array.from(groupedEntries.keys()));
  const posterTimezone = entries.at(0)?.timezone;
  const displayTimezone = resolveTimezone(posterTimezone);
  const timezoneAbbr = formatInTimeZone(new Date(), displayTimezone, 'zzz');

  return (
    <VStack align="stretch" spacing={4} width="100%">
      <HStack justify="space-between" pb={2}>
        {/* Timezone Notice */}
        <Text fontSize="sm" color="text.muted" fontStyle="italic">
          {posterTimezone
            ? `In patient's timezone (${timezoneAbbr})`
            : `In your local time (${timezoneAbbr})`}
        </Text>

        {/* Expand All Toggle */}
        <HStack spacing={2}>
          <Text fontSize="sm" color="text.default" fontWeight={500}>
            Expand All
          </Text>
          <Switch
            isChecked={allExpanded}
            onChange={(e) => setAllExpanded(e.target.checked)}
            size="md"
            data-testid="expand-all-toggle"
          />
        </HStack>
      </HStack>

      {sortedDates.map((date, index) => {
        const dayEntries = groupedEntries.get(date) || [];

        // Show the last-appointment indicator between the newest date
        // that is still after the appointment and the first date that
        // falls on or before it (dates are sorted newest-first).
        const showIndicatorBefore =
          !!lastAppointmentDate &&
          date <= lastAppointmentDate &&
          (index === 0 || sortedDates[index - 1] > lastAppointmentDate);

        return (
          <Box key={date}>
            {showIndicatorBefore && lastAppointmentDate && (
              <HStack
                spacing={3}
                mb={2}
                data-testid="last-appointment-indicator"
              >
                <Text
                  fontSize="sm"
                  fontWeight={700}
                  color="text.default"
                  whiteSpace="nowrap"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Last appointment (
                  {format(parseISO(lastAppointmentDate), 'M/d/yyyy')})
                </Text>
                <Divider borderColor="border.default" />
              </HStack>
            )}

            {/* Date Header */}
            <Text
              fontSize="sm"
              fontWeight={700}
              color="text.default"
              mb={2}
              textTransform="uppercase"
              letterSpacing="wider"
            >
              {formatDateHeader(date, displayTimezone)}
            </Text>

            {/* Entries for this date */}
            <VStack align="stretch" spacing={2}>
              {dayEntries.map((entry) => (
                <JournalEntryErrorBoundary key={entry.id}>
                  <JournalEntryDispatcher entry={entry} />
                </JournalEntryErrorBoundary>
              ))}
            </VStack>

            <Divider mt={4} />
          </Box>
        );
      })}

      {/* Load More Trigger */}
      {hasMore && (
        <Box
          ref={loadMoreTriggerRef}
          display="flex"
          justifyContent="center"
          py={4}
          data-testid="load-more-trigger"
        >
          {isLoadingMore && <Spinner size="md" />}
        </Box>
      )}

      {/* End of List */}
      {!hasMore && entries.length > 0 && (
        <Box display="flex" justifyContent="center" py={4}>
          <Text fontSize="sm" color="text.muted" data-testid="end-of-list">
            You&apos;ve reached the beginning
          </Text>
        </Box>
      )}
    </VStack>
  );
};

JournalTimeline.displayName = 'JournalTimeline';
